import axios, { AxiosError } from 'axios';
import type { LaunchRequest, LaunchResponse, PlaceBetRequest, BetDto, BetHistoryRequest } from '../types/api';

const BASE_URL = import.meta.env.VITE_BASE_URL || 'http://localhost:3000/instant-firuts';

const api = axios.create({
  baseURL: BASE_URL,
  timeout: 10000,
  headers: { 'Content-Type': 'application/json' }
});

// Request interceptor - could add auth headers here if needed
api.interceptors.request.use(
  (config) => {
    // Could add authentication headers here
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle common errors
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    // Handle 401 (unauthorized)
    if (error.response?.status === 401) {
      console.error('Unauthorized - session expired');
    }
    
    // Handle 403 (forbidden)
    if (error.response?.status === 403) {
      console.error('Forbidden - access denied');
    }
    
    // Handle 500 (server error)
    if (error.response?.status && error.response.status >= 500) {
      console.error('Server error');
    }
    
    return Promise.reject(error);
  }
);

// Retry logic with exponential backoff
async function withRetry<T>(
  fn: () => Promise<T>,
  maxRetries: number = 3,
  initialDelay: number = 1000
): Promise<T> {
  let lastError: Error | undefined;
  
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error: any) {
      lastError = error;
      
      // Don't retry client errors (4xx)
      if (axios.isAxiosError(error) && error.response?.status && error.response.status < 500) {
        throw error;
      }
      
      // Don't retry validation errors
      if (error.response?.data?.statusCode === 400) {
        throw error;
      }
      
      // Wait before retry (exponential backoff)
      if (attempt < maxRetries - 1) {
        const delay = initialDelay * Math.pow(2, attempt);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }
  
  throw lastError;
}

export async function launch(req: LaunchRequest): Promise<LaunchResponse> {
  return withRetry(() => 
    api.post<LaunchResponse>('/launch', req).then(r => r.data)
  );
}

export async function placeBet(req: PlaceBetRequest): Promise<{ bet: BetDto; balance: number }> {
  return withRetry(() => 
    api.post<{ bet: BetDto; balance: number }>('/place-bet', req).then(r => r.data)
  );
}

export async function fetchBetHistory(req: BetHistoryRequest): Promise<BetDto[]> {
  return withRetry(() => 
    api.post<BetDto[]>('/my-bets', req).then(r => r.data)
  );
}